import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Input } from '@/app/components/form/Input';
import { SingleDatePicker } from '@/app/components/form/SingleDatePicker';
import type { CreateClientFormData, RelatedPassengerBasePath } from '@/types/clientsApiTypes';
import { formatMMDDYYYYToYYYYMMDD, formatYYYYMMDDToMMDDYYYY } from '@/utils/formatDate';
import { useCreateClientPassportData } from '../../hooks/useCreateClientPassportData';
import { useGetClientPassportData } from '../../hooks/useGetClientPassportData';
import { useUpdateClientPassportData } from '../../hooks/useUpdateClientPassportData';

type RelatedPassengerTravelPreferencesTravelDataProps = {
  basePath: RelatedPassengerBasePath;
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
};

export const RelatedPassengerTravelPreferencesTravelData = ({
  basePath,
  onUpdateFormData,
}: RelatedPassengerTravelPreferencesTravelDataProps) => {
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();

  const relatedClientId = watch(`${basePath}.client.client_id`);
  const clientId = relatedClientId ? String(relatedClientId) : '';
  const passportPath = `${basePath}.passport` as const;

  const { data: passportData } = useGetClientPassportData(clientId);
  const { mutateAsync: createPassport } = useCreateClientPassportData();
  const { mutateAsync: updatePassport } = useUpdateClientPassportData();

  useEffect(() => {
    if (!clientId || !passportData?.data) return;
    const d = passportData.data.at(0) ?? null;
    if (!d) return;
    if (d.passport_number) setValue(`${passportPath}.passport_number`, d.passport_number);
    if (d.expiry_date) setValue(`${passportPath}.expiry_date`, formatYYYYMMDDToMMDDYYYY(d.expiry_date) ?? '');
    if (d.id) setValue(`${passportPath}.passport_id`, d.id);
  }, [clientId, passportData?.data, setValue, passportPath]);

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);
  };

  const passportId = watch(`${passportPath}.passport_id`) as string | null;

  const savePassport = async (passportNumber: string | null, expiryDate: string | null) => {
    if (!clientId) return;
    const expiryYYYYMMDD = expiryDate ? formatMMDDYYYYToYYYYMMDD(expiryDate) : null;
    try {
      if (passportId) {
        await updatePassport({
          passportId,
          data: { passport_number: passportNumber ?? null, expiry_date: expiryYYYYMMDD ?? null },
        });
      } else {
        if (!passportNumber?.trim() && !expiryYYYYMMDD) return;
        await createPassport(
          {
            clientId,
            data: { passport_number: passportNumber?.trim() ?? '', expiry_date: expiryYYYYMMDD ?? '' },
          },
          {
            onSuccess: (data) => {
              setValue(`${passportPath}.passport_id`, data.data.id);
              setValue(`${passportPath}.passport_number`, data.data.passport_number ?? null);
              setValue(`${passportPath}.expiry_date`, data.data.expiry_date ?? null);
            },
          }
        );
      }
    } catch {
      toast.error('Failed to save passport data');
    }
  };

  const handlePassportNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value?.trim() || null;
    const expiry = watch(`${passportPath}.expiry_date`) as string | null | undefined;
    savePassport(value, expiry ?? null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      <Input
        {...register(`${basePath}.tsa.global_entry`)}
        intent="primaryWhite"
        label="Global entry number"
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      <Controller
        name={`${basePath}.tsa.global_expiry_date`}
        control={control}
        render={({ field }) => (
          <SingleDatePicker
            name={`${basePath}.tsa.global_expiry_date`}
            label="Global entry expiry date"
            intent="primaryWhite"
            minDate={new Date()}
            errors={errors}
            value={field.value || ''}
            onChangeDate={(date) => {
              field.onChange(date);
              onUpdateFormData?.(`${basePath}.tsa.global_expiry_date`, formatMMDDYYYYToYYYYMMDD(date));
            }}
          />
        )}
      />

      <Input
        {...register(`${basePath}.tsa.known_traveler_number`)}
        intent="primaryWhite"
        label="Known traveler number (KTN)"
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      <Controller
        name={`${basePath}.tsa.known_traveler_number_expire_date`}
        control={control}
        render={({ field }) => (
          <SingleDatePicker
            name={`${basePath}.tsa.known_traveler_number_expire_date`}
            label="KTN expiry date"
            intent="primaryWhite"
            errors={errors}
            minDate={new Date()}
            value={(field.value as string) || ''}
            onChangeDate={(date) => {
              field.onChange(date);
              onUpdateFormData?.(`${basePath}.tsa.known_traveler_number_expire_date`, formatMMDDYYYYToYYYYMMDD(date));
            }}
          />
        )}
      />

      <Input
        {...register(`${passportPath}.passport_number`)}
        intent="primaryWhite"
        label="Passport number"
        onBlur={handlePassportNumberBlur}
      />

      <Controller
        name={`${passportPath}.expiry_date`}
        control={control}
        render={({ field }) => (
          <SingleDatePicker
            name={`${passportPath}.expiry_date`}
            label="Passport expiry date"
            minDate={new Date()}
            intent="primaryWhite"
            errors={errors}
            value={(field.value as string) || ''}
            onChangeDate={(dateStr) => {
              if (dateStr) {
                field.onChange(dateStr);
                savePassport((watch(`${passportPath}.passport_number`) as string) ?? null, dateStr);
              } else {
                field.onChange('');
                savePassport((watch(`${passportPath}.passport_number`) as string) ?? null, null);
              }
            }}
          />
        )}
      />
    </div>
  );
};
